import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { Field, reduxForm } from 'redux-form';
import { required } from 'services/validationMsgs';
import WrappedTextField from 'components/WrappedTextField/WrappedTextField';
import { uiState } from 'react-redux-ui-state';
import RBACDeleteButton from 'components/RBACWrapper/RBACDeleteButton/RBACDeleteButton';
import roleDisabled from 'services/roleDisabled/index';
import ORTypography from 'components/ORTypography/ORTypography';
import { OWNER, ADMIN, OR_ADMIN, OR_SUPERUSER } from 'constants/roles';

const propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  service: PropTypes.string.isRequired,
  deleteInstance: PropTypes.func.isRequired,
  history: PropTypes.shape({}).isRequired,
  classes: PropTypes.shape({}).isRequired,
  setUiState: PropTypes.func.isRequired,
  instanceToDelete: PropTypes.string.isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export function UnstyledDeleteSection({
  id,
  name,
  service,
  deleteInstance,
  history,
  classes,
  setUiState,
  instanceToDelete,
  userRoles,
}) {
  const redirectToInstances = () => history.push('/mission-ctrl/instances');
  const updateInstanceToDelete = (instance) =>
    setUiState({
      instanceToDelete: instance,
    });
  const instanceNameMatch = () => instanceToDelete !== name;
  const allowedUserRoles = [OWNER, ADMIN, OR_ADMIN, OR_SUPERUSER];

  return (
    <Card elevation={0} className={classes.dangerCard}>
      <CardContent className={classes.dangerCardContent}>
        <ORTypography component="h2" variant="h5">
          Remove Instance
        </ORTypography>
        <ORTypography className={classes.dangerText}>
          Confirm that you want to delete this instance by entering the name below
        </ORTypography>
        <div className={classes.form}>
          <Field
            name="name"
            label="Instance Name (Case Sensitive)"
            placeholder="Instance Name"
            variant="outlined"
            props={{
              InputLabelProps: {
                shrink: true,
                className: classes.inputLabel,
              },
            }}
            validate={[required]}
            onChange={(event) => updateInstanceToDelete(event.target.value)}
            component={WrappedTextField}
            className={classes.nameField}
            fullWidth
            disabled={roleDisabled(userRoles, allowedUserRoles)}
          />
          <div className={classes.deleteButtonContainer}>
            <RBACDeleteButton
              size="medium"
              userAccess={userRoles}
              allowedAccess={allowedUserRoles}
              invalidField={instanceNameMatch()}
              onClick={() => deleteInstance(id, service.toLowerCase(), redirectToInstances)}
              classes={{ listItemDeleteRoot: classes.makeSquared }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

UnstyledDeleteSection.propTypes = propTypes;

const styles = (theme) => ({
  dangerCard: {
    border: `1px solid ${theme.customColors.lightGrey}`,
    borderRadius: 0,
  },
  dangerCardContent: {
    display: 'flex',
    flexFlow: 'column nowrap',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(4),

    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1),
    },
  },
  dangerText: {
    color: '#6C6D6D',
    font: 'italic 12px Roboto',
  },
  deleteButtonContainer: {
    display: 'flex',

    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-end',
    },
  },
  deleteButton: {
    backgroundColor: theme.customColors.red,
    color: theme.customColors.white,
  },
  form: {
    display: 'flex',
    flexFlow: 'row nowrap',
    flex: '1 0 100%',
    '& > *': {
      marginTop: '1.5rem',
      marginLeft: theme.spacing(0.5),
    },

    [theme.breakpoints.down('sm')]: {
      flexFlow: 'column wrap',
    },
  },
  inputLabel: {
    color: theme.palette.text.primary,
  },
  nameField: {
    width: '95%',
    '& label': {
      color: theme.customColors.black,
    },
    marginTop: theme.spacing(2),

    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  makeSquared: {
    borderRadius: '4px',
  },
});

export const uiConfig = {
  name: 'instanceDeleteItem',
  state: () => ({
    instanceToDelete: '',
  }),
};

export default uiState(uiConfig)(
  withStyles(styles)(reduxForm({ form: 'customConfig' })(UnstyledDeleteSection)),
);
